export const iamAdminEn = {
  title: 'Security Governance',
  nav: {
    overview: 'Overview',
    dataScopes: 'Data Scopes',
    roleDataScopes: 'Role × Resource',
    fieldPermissions: 'Field Permissions',
    emergencyBypass: 'Emergency Bypass',
    auditLogs: 'Audit Logs',
    help: 'User Guide',
  },
  common: {
    loading: 'Loading…',
    empty: 'No data',
    loadFailed: 'Failed to load',
    saved: 'Saved',
    saveFailed: 'Save failed',
    deleted: 'Deleted',
    deleteFailed: 'Delete failed',
    submitting: 'Submitting…',
    confirm: 'Confirm',
    cancel: 'Cancel',
    fillRequired: 'Please fill in all required fields',
    actions: 'Actions',
    all: 'All',
    pickRole: 'Pick role',
    pickResource: 'Select resource',
    pickScopeType: 'Select scopeType (CUSTOM is permanently disabled)',
  },
  scopeType: {
    SELF: { label: 'SELF', hint: 'Records created by self only' },
    DEPARTMENT: { label: 'DEPARTMENT', hint: 'Own department (excludes children)' },
    DEPARTMENT_TREE: { label: 'DEPARTMENT_TREE', hint: 'Own department + all sub-departments' },
    ORGANIZATION: { label: 'ORGANIZATION', hint: 'Own organization + global records' },
    REGION: { label: 'REGION', hint: 'Current currentRegion context' },
    ALL: { label: 'ALL', hint: 'All data (broadest)' },
  },
  fieldAccess: {
    VISIBLE: 'Visible & editable (default)',
    READONLY: 'Visible, not editable',
    HIDDEN: 'Completely hidden',
    DESENSITIZE: 'Masked',
  },
  overview: {
    subtitle: 'Ops & compliance view of IAM: emergency bypass + audit logs',
    totalCount: 'Total',
    activeCount: 'Active',
    dataScopesDesc: 'Manage DataScope definitions (SELF / DEPARTMENT / ORGANIZATION, etc.)',
    roleDataScopesDesc: 'Bind Role × Resource to a DataScope',
    fieldPermissionsDesc: 'Configure visible / masked / hidden access per (role, resource, field)',
    emergencyBypassDesc: 'Production emergency: temporarily skip permission check on an endpoint (max 4h)',
    auditLogsDesc: 'All IAM configuration changes and admin bypass entries',
    rbacConfigTitle: 'RBAC Configuration',
    rbacConfigDesc: 'Role permissions, data scope binding, and field permissions are configured in Organization',
    openRoles: 'Roles',
    openDataScopes: 'DataScope Catalog',
  },
  dataScopes: {
    title: 'DataScope Definitions',
    subtitle: 'Built-in DataScopes cannot be deleted; CUSTOM is permanently disabled',
    newButton: 'New',
    editTitle: 'Edit DataScope',
    builtIn: 'Built-in',
    custom: 'Custom',
    builtInImmutable: 'Built-in DataScope cannot be deleted',
    confirmDelete: 'Delete {code}?',
    codeHint: 'Uppercase letters / digits / underscore only. Cannot be changed once created',
    columns: {
      code: 'Code',
      name: 'Name',
      scopeType: 'Scope Type',
      builtIn: 'Type',
      actions: 'Actions',
    },
  },
  roleDataScopes: {
    title: 'Role × Resource → DataScope',
    subtitle: 'Same role can bind different DataScopes per resource; resource = * is the default fallback',
    bindButton: 'New Binding',
    bindTitle: 'Bind DataScope',
    pickRole: 'Pick role',
    pickScope: 'Pick DataScope',
    confirmUnbind: 'Unbind this entry? The role will fall back to default scope for this resource',
    manageCatalog: 'Manage DataScope catalog →',
    columns: {
      resource: 'Resource',
      dataScope: 'DataScope',
      scopeType: 'Scope Type',
    },
  },
  fieldPermissions: {
    title: 'Field-level Permissions',
    subtitle: 'Control access level of (role, resource, field). Multi-role takes the most permissive.',
    newButton: 'New',
    editTitle: 'Edit Field Permission',
    confirmDelete: 'Delete this field permission rule?',
    filterRole: 'Filter by role',
    filterResource: 'Filter by resource',
    filterResourcePlaceholder: 'e.g. purchase_order',
    columns: {
      role: 'Role',
      resource: 'Resource',
      field: 'Field',
      access: 'Access Level',
    },
  },
  emergencyBypass: {
    title: 'Emergency Bypass',
    subtitle: 'Production incident channel. Bypass auto-expires after at most 4 hours.',
    warning: '⚠️ Enabling bypass lets the endpoint skip permission checks. Use only for blocking incidents. Every enable is audited.',
    newButton: 'Enable Bypass',
    confirmEnable: 'Enable',
    dialogDesc: 'After enabling, all users skip PermissionsGuard for this endpoint within TTL.',
    confirmDisable: 'Disable bypass for {endpoint}?',
    empty: 'No active bypass',
    ttlLabel: 'TTL (minutes)',
    ttlHint: 'Max 240 minutes (4h)',
    ttlMaxError: 'TTL cannot exceed 240 minutes',
    endpointHint: 'Format "METHOD /path", e.g. "POST /api/v1/users"',
    columns: {
      endpoint: 'Endpoint',
      reason: 'Reason',
      enabledAt: 'Enabled At',
      expiresAt: 'Expires At',
    },
  },
  auditLogs: {
    title: 'IAM Audit Logs',
    subtitle: 'All changes to DataScope / RoleDataScope / FieldPermission / Emergency Bypass / Administrator bypass',
    search: 'Search',
    viewDiff: 'View diff',
    diffTitle: 'Change Details',
    diffBefore: 'Before',
    diffAfter: 'After',
    totalLabel: 'Total',
    prev: 'Prev',
    next: 'Next',
    filters: {
      actor: 'actor (UUID)',
      action: 'action',
      resource: 'resource',
    },
    columns: {
      timestamp: 'Timestamp',
      action: 'Action',
      resource: 'Resource',
      actor: 'Actor',
      targetId: 'Target ID',
    },
  },

  help: {
    title: 'User Guide',
    subtitle: 'Quick overview of the IAM governance console',
    intro: {
      title: 'What is this',
      paragraph:
        'The IAM governance console is the single entry point for senior administrators to manage DataScope (row-level visibility), field-level masking, emergency bypass, delegations and access reviews. Every admin action is recorded in a dedicated IAM audit log. Some pages (DataScope / FieldPermission UIs) are still on the roadmap; what is live today is Emergency Bypass and IAM Audit Logs.',
      highlights: [
        { title: 'Governance UI', desc: 'Replaces SQL / Redis CLI workflows for permission governance' },
        { title: 'Dedicated audit', desc: 'iam_audit_log records admin governance actions, separate from business audit' },
        { title: 'Emergency bypass', desc: 'TTL-bound temporary backdoor for unblocking stuck flows' },
      ],
    },
    concepts: {
      title: 'Core Concepts',
      description: 'The four IAM layers and key terms',
      items: [
        { term: 'Four IAM layers', desc: 'L1 Role / Permission — who can call; L2 OrganizationScope — which org; L3 DataScope — which rows; L4 FieldPermission — which fields' },
        { term: 'DataScope', desc: 'Row-level visibility. Built-in: ALL / ORGANIZATION / DEPARTMENT_TREE (own + subordinate) / SELF. CUSTOM is permanently disabled' },
        { term: 'Role × Resource binding', desc: 'A role can bind different DataScopes to different resources (e.g. Sales: Order=DEPARTMENT_TREE, Customer=ORGANIZATION)' },
        { term: 'FieldPermission', desc: 'Field-level access rules; accessLevel = READ / READ_MASKED (masked) / HIDDEN' },
        { term: 'EmergencyBypass', desc: 'Temporary backdoor on a specific endpoint with a TTL (typically capped at 24h); fully audited' },
        { term: 'Delegation', desc: 'Temporarily delegate permissions to a colleague during leave' },
        { term: 'Access Review', desc: 'Periodic review (recommended quarterly): confirm role assignments still needed, prevent permission creep' },
        { term: 'iam_audit_log', desc: 'Dedicated audit table for IAM governance actions; separate from business audit_log' },
      ],
    },
    roles: {
      title: 'Roles & Access',
      description: 'This module targets senior administrators',
      columnHeaders: { role: 'Role', scope: 'Can do', desc: 'Description' },
      items: [
        { code: 'Administrator', name: 'System Admin', scope: 'Everything', desc: 'Full access to the governance console' },
        { code: 'SecurityAdmin', name: 'Security / SOX owner', scope: 'Everything', desc: 'Same; reviews and audit queries are their daily work' },
        { code: 'Operations', name: 'IT Operations', scope: 'Bypass + Audit', desc: 'Used in incident response' },
        { code: 'Business User', name: 'Business user', scope: 'Own delegations only', desc: 'Sees own delegation records on organization/delegations' },
      ],
    },
    tasks: {
      title: 'Common Tasks',
      description: 'Available entry points today (planned pages will be added later)',
      cards: [
        { id: 'overview', name: 'Overview', desc: 'Governance dashboard with entry cards' },
        { id: 'emergencyBypass', name: 'Emergency Bypass', desc: 'View active bypasses, create TTL-bound bypass, revoke immediately' },
        { id: 'auditLogs', name: 'IAM Audit Logs', desc: 'Search governance actions by actor / action / resource / time range' },
      ],
    },
  },
};
